//hooks
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

//components
import {
  Section,
  SectionWrap,
  Kicker,
  KickerDot,
  SectionTitle,
  SectionLede,
  Grad,
} from '@/containers-ai/SectionHeading';

//styles
import {
  RailsGrid,
  RailCard,
  DiagramSvg,
  FlowPath,
  RailStack,
  StepRow,
  StepNumber,
  StepTitle,
  StepDescription,
} from './Rails.styles';

const steps = [
  {
    number: '01',
    title: 'Deploys into your stack',
    description:
      'Agents go to work inside the systems you already use. Nothing gets ripped out, nothing migrates.',
  },
  {
    number: '02',
    title: 'Acts on your loads',
    description:
      'Quotes, tenders, appointments, settlement — executed live inside your operation, under the rules you set.',
  },
  {
    number: '03',
    title: 'Writes back to your system of record',
    description:
      'Your TMS and ERP stay the source of truth. Every outcome lands where your team already works.',
  },
];

export function Rails() {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <Section id="rails">
      <SectionWrap>
        <Kicker>
          <KickerDot />
          Integrations
        </Kicker>
        <SectionTitle>
          It runs in your environment, <Grad>not instead of it.</Grad>
        </SectionTitle>
        <SectionLede>
          Loadsmart AI doesn&rsquo;t need to replace your systems. It plugs
          into it &mdash; over APIs, EDI and MCP &mdash; puts agents to work
          inside your operation, and leaves the record where your team
          already lives.
        </SectionLede>

        <RailsGrid>
          <RailCard>
            <DiagramSvg
              viewBox="0 0 520 300"
              role="img"
              aria-label="Loadsmart AI connected by two-way lines to the systems you already run and to carriers and facilities: work flows in, outcomes flow back."
            >
              <defs>
                <linearGradient id="g2" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#00D7D7" />
                  <stop offset="1" stopColor="#14D64D" />
                </linearGradient>
              </defs>
              <g
                fontFamily="Manrope,system-ui,sans-serif"
                fontSize="10"
                fontWeight="600"
                letterSpacing="1.5"
                fill="#6C8583"
              >
                <text x="85" y="26" textAnchor="middle">THE SYSTEMS YOU RUN</text>
                <text x="455" y="106" textAnchor="middle" letterSpacing="1">THE FREIGHT WORLD</text>
              </g>
              <g fill="none" stroke="#2A5B60" strokeWidth="1.3">
                <FlowPath d="M156 60 C 202 60 212 130 250 142" />
                <FlowPath d="M156 150 C 202 150 206 150 250 150" />
                <FlowPath d="M156 240 C 202 240 212 170 250 158" />
                <FlowPath d="M336 150 C 380 150 384 150 412 150" />
              </g>
              <g fontFamily="Manrope,system-ui,sans-serif" fontSize="10" fontWeight="500" fill="#6C8583">
                <text x="196" y="50">API</text>
                <text x="196" y="141">EDI</text>
                <text x="196" y="254">MCP</text>
              </g>
              <g fontFamily="Manrope,system-ui,sans-serif" fontWeight="600" fontSize="12" fill="#EAF4F1">
                <rect x="20" y="36" width="130" height="44" rx="9" fill="#0A222A" stroke="#2A5B60" />
                <text x="85" y="62" textAnchor="middle">Your TMS</text>
                <rect x="20" y="128" width="130" height="44" rx="9" fill="#0A222A" stroke="#2A5B60" />
                <text x="85" y="154" textAnchor="middle">ERP &middot; WMS</text>
                <rect x="20" y="218" width="130" height="44" rx="9" fill="#0A222A" stroke="#2A5B60" />
                <text x="85" y="244" textAnchor="middle">Email &middot; EDI</text>
                <rect x="420" y="122" width="84" height="56" rx="10" fill="#0A222A" stroke="#2A5B60" />
                <text x="462" y="147" textAnchor="middle" fontSize="11">Carriers &amp;</text>
                <text x="462" y="162" textAnchor="middle" fontSize="11">facilities</text>
              </g>
              <rect x="258" y="118" width="72" height="64" rx="12" fill="#0E2A31" stroke="url(#g2)" strokeWidth="2" />
              <text x="294" y="146" textAnchor="middle" fontFamily="Manrope,system-ui,sans-serif" fontSize="12" fontWeight="800" fill="#fff">Loadsmart</text>
              <text x="294" y="163" textAnchor="middle" fontFamily="Manrope,system-ui,sans-serif" fontSize="12" fontWeight="800" fill="#14D64D">AI</text>
              {!prefersReducedMotion && (
                <g>
                  <circle r="3.4" fill="#00D7D7">
                    <animateMotion
                      dur="4.2s"
                      repeatCount="indefinite"
                      keyPoints="0;1;0"
                      keyTimes="0;.5;1"
                      calcMode="linear"
                      path="M156 60 C 202 60 212 130 250 142"
                    />
                  </circle>
                  <circle r="3.4" fill="#14D64D">
                    <animateMotion
                      dur="4.6s"
                      repeatCount="indefinite"
                      begin="-1.6s"
                      keyPoints="0;1;0"
                      keyTimes="0;.5;1"
                      calcMode="linear"
                      path="M156 150 C 202 150 206 150 250 150"
                    />
                  </circle>
                  <circle r="3.4" fill="#14D64D">
                    <animateMotion
                      dur="3.8s"
                      repeatCount="indefinite"
                      begin="-.7s"
                      keyPoints="0;1;0"
                      keyTimes="0;.5;1"
                      calcMode="linear"
                      path="M336 150 C 380 150 384 150 412 150"
                    />
                  </circle>
                  <circle r="3.4" fill="#00D7D7">
                    <animateMotion
                      dur="5s"
                      repeatCount="indefinite"
                      begin="-2.4s"
                      keyPoints="0;1;0"
                      keyTimes="0;.5;1"
                      calcMode="linear"
                      path="M156 240 C 202 240 212 170 250 158"
                    />
                  </circle>
                </g>
              )}
            </DiagramSvg>
          </RailCard>

          <RailStack>
            {steps.map((step) => (
              <StepRow key={step.number}>
                <StepNumber>{step.number}</StepNumber>
                <div>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </div>
              </StepRow>
            ))}
          </RailStack>
        </RailsGrid>
      </SectionWrap>
    </Section>
  );
}
